import {assert} from 'Asserts';
import {Tag} from 'folder-items/FileConstants';
import {ClientId} from 'notebook/ELNConstants';
import {SchemaFieldColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import toColumnDefinition from 'notebook/structured-tables/columns/toColumnDefinition';
import {
  GenericStructuredTableColumn,
  StructuredTableSchema,
} from 'notebook/structured-tables/StructuredTableConstants';
import doesEvaluatedTableHaveAnyErrors from 'notebook/tables/errors/doesEvaluatedTableHaveAnyErrors';
import {EvaluatedCell, EvaluatedTable} from 'notebook/tables/excess/Evaluator';
import {TableColumnType} from 'notebook/tables/TableConstants';
import {
  convertTypedValueToTag,
  formatTypedValue,
  getProcessTaskIdFromCell,
} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType} from 'notebook/tables/typed-values/TypedValueConstants';
import {ProcessOutputTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';
import {
  CreateDirectExecutionOutput,
  CreateOrUpdateServerProcessOutput,
  CreateServerProcessOutput,
} from 'processes/dao/ProcessManagementDAO';
import {EVALUATED_TABLE_ERROR_OPTIONS} from 'processes/ProcessConstants';
import {ProcessOutputStructuredTable} from 'processes/tables/ProcessOutputStructuredTableAdapter';
import {getInitialTag, getTagsToSet, TagUpdateObject} from 'schemas/tags/TagUtil';
import {warn} from 'util/Logging';

const checkForErrorsAndLog = (
  evaluatedTable: EvaluatedTable,
  table: ProcessOutputStructuredTable,
  sourceType?: string,
  debugInfo?: object
): void => {
  if (doesEvaluatedTableHaveAnyErrors(evaluatedTable, EVALUATED_TABLE_ERROR_OPTIONS)) {
    warn('Attempting to convert a output table with errors BNCH-40814', {
      extra: {
        sourceType,
        debugInfo,
        errors: evaluatedTable.flat().filter((cell) => cell.error != null),
        schema: table.schemas[0].id,
      },
    });
  }
};

function convertTagColumnToTag(
  structuredTableColumn: GenericStructuredTableColumn<ClientId>,
  tableSchemas: Array<StructuredTableSchema>,
  cell: EvaluatedCell
): Tag {
  assert(structuredTableColumn.columnType === TableColumnType.TAG_VALUE, 'Expected tag value column');
  const columnDefinition = toColumnDefinition({
    structuredTableColumn,
    schemas: tableSchemas,
  }) as SchemaFieldColumnDefinition;
  const tag = convertTypedValueToTag(cell.value, columnDefinition.schemaField);
  return tag == null ? getInitialTag(columnDefinition.schemaField) : tag;
}

// Exported for testing.
export function getOutputIdFromRow(row: Array<EvaluatedCell>): string | null {
  const outputCells = row.filter((cell) => cell.value.type === BaseTypedValueType.PROCESS_OUTPUT);

  assert(
    outputCells.length === 1 || outputCells.length === 0,
    'Expected row to only have one or zero cells with a value of PROCESS_OUTPUT.',
    {row}
  );
  return outputCells.length === 0 ? null : (outputCells[0].value as ProcessOutputTypedValue).id;
}

// Exported for testing.
export function getTaskIdFromRow(row: Array<EvaluatedCell>): string | null {
  const taskIds = row
    .map((cell) =>
      getProcessTaskIdFromCell({
        text: formatTypedValue(cell.value),
        typedValue: cell.value,
      })
    )
    .filter((value) => value != null);
  assert(
    taskIds.length === 1 || taskIds.length === 0,
    'Expected row to only have one or zero cells with a value of PROCESS_TASK.',
    {row}
  );
  return taskIds.length === 0 ? null : taskIds[0];
}

function getTagsToUpdateForRow(
  row: Array<EvaluatedCell>,
  columns: Array<GenericStructuredTableColumn<ClientId>>,
  schemas: Array<StructuredTableSchema>
): TagUpdateObject {
  const tags = columns.reduce((acc: Array<Tag>, column, colIdx) => {
    if (column.columnType === TableColumnType.TAG_VALUE) {
      acc.push(convertTagColumnToTag(column, schemas, row[colIdx]));
    }
    return acc;
  }, []);
  return getTagsToSet(tags);
}

export function outputTableToOutputsForCreate({
  table,
  evaluatedTable,
  creationOriginIds,
  sourceType,
  actionOrigin,
  actionMethod,
  schemaMapperId,
  fromItemIds,
  taskIds,
  debugInfo,
}: {
  table: ProcessOutputStructuredTable;
  evaluatedTable: EvaluatedTable;
  creationOriginIds: Array<string>;
  sourceType: string;
  actionOrigin: string;
  actionMethod: string;
  schemaMapperId?: string | null;
  fromItemIds?: Array<string>;
  taskIds?: Array<string>;
  debugInfo?: object;
}): Array<CreateServerProcessOutput> {
  checkForErrorsAndLog(evaluatedTable, table, sourceType, debugInfo);

  return evaluatedTable.reduce((acc: Array<CreateServerProcessOutput>, row: Array<EvaluatedCell>, rowIdx) => {
    const outputId = getOutputIdFromRow(row);

    // Skip if an output ID is present and no source column -- means that the output should be updated, not created.
    if (outputId != null) {
      return acc;
    }

    const processTaskId = taskIds != null ? taskIds[rowIdx] : getTaskIdFromRow(row);
    assert(processTaskId != null, 'Expected task ID to be present for converting table to create outputs.');
    const fields = getTagsToUpdateForRow(row, table.columns, table.schemas);
    acc.push({
      processTaskId,
      fields,
      creationOriginId: creationOriginIds[rowIdx],
      sourceType,
      actionOrigin,
      actionMethod,
      ...(fromItemIds != null ? {fromItemId: fromItemIds[rowIdx]} : {}),
      schemaMapperId: schemaMapperId ?? null,
    });
    return acc;
  }, []);
}

/**
 * Returns a 1:1 in-order mapping of Process Output specs for the specified evaluated table rows. Spec objects
 * will either be for create or update depending on if the row has been previously submitted (i.e. has an
 * existing output object associated with it).
 */
export function outputTableToOutputsForCreateOrUpdate({
  table,
  evaluatedTable,
  creationOriginIds,
  sourceType,
  actionOrigin,
  actionMethod,
  schemaMapperId,
  fromItemIds,
  taskIds,
  debugInfo,
}: {
  table: ProcessOutputStructuredTable;
  evaluatedTable: EvaluatedTable;
  creationOriginIds: Array<string>;
  sourceType: string;
  actionOrigin: string;
  actionMethod: string;
  schemaMapperId?: string | null;
  fromItemIds?: Array<string>;
  taskIds?: Array<string>;
  debugInfo?: object;
}): Array<CreateOrUpdateServerProcessOutput> {
  checkForErrorsAndLog(evaluatedTable, table, sourceType, debugInfo);

  return evaluatedTable.reduce(
    (acc: Array<CreateOrUpdateServerProcessOutput>, row: Array<EvaluatedCell>, rowIdx) => {
      const outputId = getOutputIdFromRow(row);

      if (outputId == null) {
        const processTaskId = taskIds != null ? taskIds[rowIdx] : getTaskIdFromRow(row);
        assert(
          processTaskId != null,
          'Expected task ID to be present for converting table to create outputs.'
        );
        const fields = getTagsToUpdateForRow(row, table.columns, table.schemas);
        acc.push({
          processTaskId,
          fields,
          creationOriginId: creationOriginIds[rowIdx],
          sourceType,
          actionOrigin,
          actionMethod,
          ...(fromItemIds != null ? {fromItemId: fromItemIds[rowIdx]} : {}),
          schemaMapperId: schemaMapperId ?? null,
        });
      } else {
        const fields = getTagsToUpdateForRow(row, table.columns, table.schemas);
        acc.push({
          id: outputId,
          fields,
          sourceType,
          actionOrigin,
          actionMethod,
        });
      }
      return acc;
    },
    []
  );
}

export function outputTableToCreateDirectExecutionOutputs({
  table,
  evaluatedTable,
  sourceType,
  debugInfo,
}: {
  table: ProcessOutputStructuredTable;
  evaluatedTable: EvaluatedTable;
  sourceType: string;
  debugInfo?: object;
}): Array<CreateDirectExecutionOutput> {
  checkForErrorsAndLog(evaluatedTable, table, sourceType, debugInfo);

  return evaluatedTable.reduce((acc: Array<CreateDirectExecutionOutput>, row: Array<EvaluatedCell>) => {
    const outputId = getOutputIdFromRow(row);

    if (outputId != null) {
      return acc;
    }
    const processTaskId = getTaskIdFromRow(row);
    assert(
      processTaskId != null,
      'Expected task ID to be present for converting table to create direct execution outputs.'
    );
    const fields = getTagsToUpdateForRow(row, table.columns, table.schemas);
    acc.push({
      processTaskId,
      fields,
      sourceType,
    });
    return acc;
  }, []);
}
